import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { getProject } from "api/scankit/getProject.api";

const ScanProjectContext = createContext(null);
const validId = (value) => /^[1-9]\d*$/.test(String(value)) && Number.isSafeInteger(Number(value));

export function scanPath(page, projectId, options = {}) {
  if (!validId(projectId)) throw new Error("Invalid ScanKit project ID");
  const query = new URLSearchParams({ projectId: String(projectId), ...options });
  return `/${page}?${query}`;
}

export function useScanProject() {
  const context = useContext(ScanProjectContext);
  if (!context) throw new Error("ScanKit requires a loaded project route");
  return context;
}

function LoadedScanProject({ projectId, children }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ status: "loading" });
    getProject(Cookies.get("token"), projectId)
      .then((response) => {
        if (!active) return;
        const project = response?.project;
        const modelId = project?.editors?.[0]?.models?.[0]?.id;
        if (String(project?.id) !== projectId || !validId(modelId)) {
          setState({ status: "invalid" });
          return;
        }
        setState({
          status: "ready",
          context: { identity: { projectId, modelId: String(modelId) }, project },
        });
      })
      .catch((error) => {
        if (active) setState({ status: error?.response?.status === 404 ? "invalid" : "error" });
      });
    return () => {
      active = false;
    };
  }, [projectId, attempt]);

  if (state.status === "invalid") return <Navigate to="/cardList" replace />;
  if (state.status === "error")
    return (
      <div role="alert">
        {t("interneterror")}
        <button onClick={() => setAttempt((value) => value + 1)}>{t("PleaseTryAgain")}</button>
      </div>
    );
  if (state.status !== "ready") return <div role="status">{t("pleaseWait")}</div>;
  return (
    <ScanProjectContext.Provider value={state.context}>{children}</ScanProjectContext.Provider>
  );
}

export default function ScanProjectRoute({ children }) {
  const { pathname, search } = useLocation();
  const ids = new URLSearchParams(search).getAll("projectId");
  if (ids.length !== 1 || !validId(ids[0])) return <Navigate to="/cardList" replace />;
  return (
    <LoadedScanProject key={`${pathname}:${ids[0]}`} projectId={ids[0]}>
      {children}
    </LoadedScanProject>
  );
}
